'use client';

/**
 * HomepageV3 AnnouncementBanner — now consumes the published se3
 * design-system package (`@startengine/design-system.se3.announcement-banner`,
 * exported as `Se3AnnouncementBanner`). Thin re-export adapter.
 *
 * Faithful port: identical props (children/chip/items/data-cy), identical
 * `.ipo-chip` chip class, identical default `data-cy` (`announcement-banner`)
 * and item hook (`announcement-banner-item`). The original prop interface is
 * re-declared here for type-safety (the package's generated `.d.ts` uses
 * loose `any` types).
 */
import type { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import { Se3AnnouncementBanner } from '@startengine/design-system.se3.announcement-banner';

export interface AnnouncementItem {
  /** Mono teal chip rendered before the text (`.ipo-chip`). */
  chip?: string;
  text: ReactNode;
}

export interface AnnouncementBannerProps {
  /** Single text node (use with optional `chip`). */
  children?: ReactNode;
  /** Leading mono teal chip when using `children`. */
  chip?: string;
  /** Multiple items rendered in sequence. Overrides `children`. */
  items?: AnnouncementItem[];
  className?: string;
  'data-cy'?: string;
}

const Se3Banner = Se3AnnouncementBanner as unknown as FC<AnnouncementBannerProps>;

/**
 * PD (Paige QA): the announcement banner is hidden on mobile (≤768px) — it's
 * cramped on small screens per the design. Rendered normally on desktop.
 */
const AnnouncementBanner: FC<AnnouncementBannerProps> = (props) => (
  <Box data-cy="announcement-banner-wrap" sx={{ '@media (max-width: 768px)': { display: 'none' } }}>
    <Se3Banner {...props} />
  </Box>
);

export default AnnouncementBanner;
